import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageCircle, ShieldCheck } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import BrochureDownload from '@/components/site/BrochureDownload';
import AfterSubmitSteps from '@/components/site/AfterSubmitSteps';
import AnalyticsBeacon from '@/components/AnalyticsBeacon';

// =============================================================================
// /brochure — Standalone lead-capture landing for the GENERAL Prime Vision
// brochure. Mirrors the FSE page but with the default brochure copy/asset.
//
// Same BrochureDownload mechanism — only differences: copy + leadSource +
// campaign metadata.
// =============================================================================

const COPY = {
  it: {
    metaTitle: 'Brochure Prime Vision · Software gestionale oculistico',
    back: 'Torna al sito',
    wa: 'WhatsApp',
    eyebrow: 'Brochure ufficiale · Prime Vision',
    h1_a: 'Vuoi capire se Prime Vision fa per te,',
    h1_b: 'senza parlare con nessuno?',
    sub:
      'Scarica la brochure: in pochi minuti vedi funzioni, flussi e vantaggi concreti per il tuo studio. PDF immediato, nessuna telefonata obbligatoria.',
    trustStrip: 'PDF gratuito · ricevilo subito · nessun impegno',
    footer: 'Prime Vision · Software gestionale oculistico · primevision.app',
  },
  en: {
    metaTitle: 'Prime Vision brochure · Ophthalmology practice software',
    back: 'Back to site',
    wa: 'WhatsApp',
    eyebrow: 'Official brochure · Prime Vision',
    h1_a: 'Want to know if Prime Vision fits you,',
    h1_b: 'without talking to anyone?',
    sub:
      'Download the brochure: in a few minutes you see features, flows and concrete benefits for your practice. Instant PDF, no mandatory phone call.',
    trustStrip: 'Free PDF · instant · no commitment',
    footer: 'Prime Vision · Ophthalmology practice management · primevision.app',
  },
};

export const BrochurePage = () => {
  const { locale } = useLanguage();
  const c = COPY[locale] || COPY.it;

  useEffect(() => {
    document.title = c.metaTitle;
  }, [c.metaTitle]);

  return (
    <main
      className="min-h-screen bg-white text-pv-ink relative overflow-x-hidden"
      data-testid="pv-brochure-page"
    >
      <header className="relative z-10 border-b border-slate-200/60 bg-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 md:h-18 flex items-center justify-between">
          <Link to="/" data-testid="pv-brochure-page-logo" className="inline-flex items-center gap-2">
            <img src="/assets/prime-vision-logo.png" alt="Prime Vision" className="h-8 md:h-9" />
          </Link>
          <div className="flex items-center gap-3 md:gap-5">
            <a
              href="https://wa.me/393520526856?text=Ciao!%20Vorrei%20info%20su%20Prime%20Vision."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pv-brochure-page-wa"
              className="hidden sm:inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-600 hover:text-[#25D366] transition-colors"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              {c.wa}
            </a>
            <Link
              to="/"
              data-testid="pv-brochure-page-back"
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-600 hover:text-pv-teal transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {c.back}
            </Link>
          </div>
        </div>
      </header>

      <section className="relative z-10 pt-8 md:pt-14 pb-4 md:pb-6">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="inline-flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-pv-orange" />
            <span className="text-[11px] md:text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              {c.eyebrow}
            </span>
          </div>
          <h1 className="mt-5 font-heading font-bold tracking-normal leading-[1.05] text-pv-ink text-[34px] sm:text-[44px] md:text-[54px] max-w-3xl">
            {c.h1_a} <span className="text-pv-teal">{c.h1_b}</span>
          </h1>
          <p className="mt-5 md:mt-6 text-slate-600 text-[15.5px] md:text-[17px] leading-[1.65] max-w-2xl">
            {c.sub}
          </p>
          <div className="mt-5 md:mt-6 inline-flex items-center gap-2 text-[12.5px] md:text-[13px] font-semibold text-slate-500">
            <ShieldCheck className="w-4 h-4 text-pv-teal" />
            {c.trustStrip}
          </div>
        </div>
      </section>

      <AfterSubmitSteps variant="brochure" testId="pv-brochure-after-steps" />

      <section className="relative z-10 pb-16 md:pb-24" data-testid="pv-brochure-lead-wrapper">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <BrochureDownload
            embedded
            hideCopyColumn
            id="brochure"
            testIdPrefix="pv-brochure-page-form"
            leadSource="brochure-page"
            fallbackSource="brochure-page"
            campaign="brochure-page"
            captureUTM
          />
        </div>
      </section>

      <footer className="relative z-10 border-t border-slate-200/60 bg-white py-6">
        <div className="max-w-5xl mx-auto px-5 md:px-8 text-center text-[11.5px] text-slate-500 font-medium">
          {c.footer}
        </div>
      </footer>

      <AnalyticsBeacon locale={locale} />
    </main>
  );
};

export default BrochurePage;
